<template>
  <div class="comments-block">
    <div class="comments-head">{{ t('comments.title') }}</div>

    <div v-if="loading" class="cm-loading">{{ t('common.loading') }}</div>

    <template v-else>
      <ul class="cm-list">
        <li v-for="c in comments" :key="c.id" class="cm-item">
          <span class="cm-avatar" :class="c.author_type === 'client' ? 'is-client' : 'is-internal'">{{ initials(c.author_name) }}</span>
          <div class="cm-body">
            <div class="cm-meta">
              <span class="cm-author">{{ c.author_name }}</span>
              <span v-if="c.author_type === 'client'" class="cm-tag">{{ t('roles.client') }}</span>
              <span class="cm-time">{{ formatTime(c.created_at) }}</span>
            </div>
            <div class="cm-text">{{ c.body }}</div>
          </div>
        </li>
        <li v-if="comments.length === 0" class="cm-empty">{{ t('comments.empty') }}</li>
      </ul>

      <div v-if="canComment" class="cm-add">
        <textarea
          v-model="draft"
          class="cm-input"
          rows="2"
          :placeholder="t('comments.placeholder')"
          @keydown.ctrl.enter="send"
        ></textarea>
        <button class="btn btn-sm btn-primary" :disabled="!draft.trim() || sending" @click="send">
          {{ t('comments.send') }}
        </button>
      </div>
      <p v-else class="cm-closed">{{ t('comments.closed') }}</p>
    </template>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { onRealtime } from '@/composables/useRealtime'

export default {
  name: 'TaskComments',
  props: {
    taskId: { type: Number, required: true },
    canComment: { type: Boolean, default: true },
  },
  setup(props) {
    const { t, locale } = useI18n()
    const comments = ref([])
    const loading  = ref(true)
    const draft    = ref('')
    const sending  = ref(false)

    async function load() {
      loading.value = true
      try { comments.value = (await api.getComments(props.taskId)).data || [] } catch {} finally { loading.value = false }
    }
    async function send() {
      const body = draft.value.trim()
      if (!body) return
      sending.value = true
      try { comments.value = (await api.addComment(props.taskId, body)).data || []; draft.value = '' } catch {} finally { sending.value = false }
    }

    const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    function formatTime(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt.replace(' ', 'T')).toLocaleString(loc, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    }

    // Live-append when anyone (e.g. a client) comments on this task — no collapse,
    // no manual refresh. Reloads the thread in place; ignores other tasks' events.
    async function reloadQuiet() {
      try { comments.value = (await api.getComments(props.taskId)).data || [] } catch {}
    }
    let offRealtime = () => {}
    onMounted(() => {
      load()
      offRealtime = onRealtime('task.changed', (e) => {
        if (e && Number(e.id) === props.taskId && (!e.action || e.action === 'comment')) reloadQuiet()
      })
    })
    onUnmounted(() => offRealtime())

    return { t, comments, loading, draft, sending, send, initials, formatTime }
  },
}
</script>

<style scoped>
.comments-block {
  margin: 4px 8px 6px;
  padding: 10px 14px 12px;
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 var(--radius) var(--radius);
  background: var(--surface-2, var(--surface));
}
.comments-head {
  font-size: 10px; text-transform: uppercase; letter-spacing: .05em;
  color: var(--muted); font-weight: 600; margin-bottom: 10px;
}
.cm-loading, .cm-empty { font-size: 12px; color: var(--muted); padding: 4px 0; }

.cm-list { list-style: none; margin: 0 0 10px; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.cm-item { display: flex; gap: 10px; align-items: flex-start; }
.cm-avatar {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: grid; place-items: center; color: #fff; font-size: 10px; font-weight: 700;
}
.cm-avatar.is-internal { background: var(--accent); }
.cm-avatar.is-client   { background: var(--high, #A66E08); }
.cm-body { min-width: 0; flex: 1; }
.cm-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cm-author { font-size: 13px; font-weight: 600; }
.cm-tag {
  font-size: 10px; text-transform: uppercase; letter-spacing: .04em;
  background: var(--accent-wash); color: var(--accent); border-radius: 999px; padding: 1px 7px;
}
.cm-time { font-size: 11px; color: var(--muted); }
.cm-text { font-size: 13px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; margin-top: 2px; }

.cm-add { display: flex; gap: 8px; align-items: flex-end; }
.cm-input {
  flex: 1; resize: vertical; min-height: 38px;
  border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px;
  font: inherit; font-size: 13px; background: var(--surface); color: var(--text);
}
.cm-input:focus { border-color: var(--accent); outline: none; }
.cm-closed { font-size: 12px; color: var(--muted); font-style: italic; padding: 4px 0; }

@media (max-width: 768px) {
  .cm-add { flex-direction: column; align-items: stretch; }
}
</style>
